import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation } from "@apollo/client";
import { getGraphQLErrorMessage } from "utils/graphqlError";
import { UPSERT_LOAN_PRODUCT_REMINDER_SCHEDULE } from "graphql/loans";

const LoanProductReminderDialog = ({
  open,
  onClose,
  loanProduct,
  schedule,
  onSaved,
}) => {
  const [daysBeforeDue, setDaysBeforeDue] = useState("");
  const [friendlyError, setFriendlyError] = useState("");

  useEffect(() => {
    if (!open) {
      setDaysBeforeDue("");
      setFriendlyError("");
      return;
    }

    setDaysBeforeDue(
      schedule?.daysBeforeDue !== null && schedule?.daysBeforeDue !== undefined
        ? String(schedule.daysBeforeDue)
        : ""
    );
    setFriendlyError("");
  }, [open, schedule]);

  const [upsertReminderSchedule, { loading }] = useMutation(
    UPSERT_LOAN_PRODUCT_REMINDER_SCHEDULE,
    {
      onError: (err) => setFriendlyError(getGraphQLErrorMessage(err)),
      onCompleted: async () => {
        setFriendlyError("");
        if (onSaved) await onSaved();
      },
    }
  );

  const canSubmit = useMemo(() => {
    const value = Number(daysBeforeDue);
    return Number.isInteger(value) && value >= 0 && value <= 60;
  }, [daysBeforeDue]);

  const handleSubmit = async () => {
    if (!loanProduct?.id || !canSubmit) return;

    await upsertReminderSchedule({
      variables: {
        newSchedule: {
          id: schedule?.id ?? null,
          loanProductId: Number(loanProduct.id),
          daysBeforeDue: Number(daysBeforeDue),
        },
      },
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Loan Product Reminder</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Product
            </Typography>
            <Typography variant="subtitle1" fontWeight={600}>
              {loanProduct?.name || "-"}
            </Typography>
          </Box>

          {schedule ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                size="small"
                color={schedule?.isActive ? "success" : "default"}
                label={schedule?.isActive ? "Active reminder" : "Inactive reminder"}
              />
              <Typography variant="caption" color="text.secondary">
                Existing schedule will be updated.
              </Typography>
            </Stack>
          ) : (
            <Alert severity="info">No reminder has been configured for this product yet.</Alert>
          )}

          <TextField
            fullWidth
            type="number"
            label="Days before due date"
            value={daysBeforeDue}
            onChange={(e) => setDaysBeforeDue(e.target.value)}
            inputProps={{ min: 0, max: 60 }}
            helperText="Use 0 to remind on due date. Allowed range: 0-60 days."
            disabled={loading}
          />

          {friendlyError ? <Alert severity="error">{friendlyError}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit || loading}>
          {loading ? "Saving..." : "Save Reminder"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LoanProductReminderDialog;
